import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import { cloneDeep, noop } from "lodash";
import { useFieldArray } from "react-hook-form";
import { Box } from "../../Box";
import { FormHideable } from "../FormHideable/FormHideable";
const EMPTY_CONTEXT = {
    insertField: noop,
    maxGroups: 0,
    minGroups: 0,
    numGroups: 0,
    prefix: "",
    remove: noop,
    swap: noop,
};
export const FormRepeatedGroupContext = React.createContext(EMPTY_CONTEXT);
/**
 * FormRepeatedGroup is a wrapper around the `useFieldArray` functionality from `react-hook-form`.
 * The children of FormRepeatedGroup are rendered for each mapping of the field array.
 *
 * https://legacy.react-hook-form.com/api/usefieldarray
 *
 * FormRepeatedGroup is expected to have a single child that is a FormFieldGroup, which then contains
 * the set of fields and controls for each repeated element in the group.
 *
 * @param props
 * @constructor
 */
export const FormRepeatedGroup = (props) => {
    const { children, defaultField: defaultFieldValue, max = 20, min = 1, prefix, hidden } = props;
    const { fields, insert, remove, swap } = useFieldArray({ name: prefix });
    // Create a "defaultField" object and assign empty values
    // which we need when using `insert` from `react-hook-form`
    const defaultField = useMemo(() => cloneDeep(defaultFieldValue), [defaultFieldValue]);
    // Wrap the react-hook-form insert call with a wrapper that can provide the value(s) for new fields
    const insertField = useCallback((index) => {
        const newField = Object.assign({}, defaultField);
        insert(index, newField, {
            shouldFocus: false,
        });
    }, [defaultField, insert]);
    const contextValue = React.useMemo(() => ({ insertField, maxGroups: max, minGroups: min, numGroups: fields.length, prefix, remove, swap }), [insertField, swap, remove, max, min, fields, prefix]);
    // Create fields if we have fewer than the specified `min`
    useEffect(() => {
        const missingFields = min - fields.length;
        if (missingFields <= 0) {
            return;
        }
        for (let i = 0; i < missingFields; i += 1) {
            insertField(fields.length + i);
        }
    }, [min, fields, insertField]);
    return (React.createElement(FormHideable, { hidden: hidden, animate: true },
        React.createElement(FormRepeatedGroupContext.Provider, { value: contextValue }, fields.map((field, index) => (React.createElement(RepeatedGroupContainer, { key: field.id, "data-testid": `RepeatedGroup.${index}.${prefix}` }, children ? children({ index, prefix }) : null))))));
};
const RepeatedGroupContainer = styled(Box) `
  gap: 4px;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;
